import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextPos } from './Pos';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { PosCashierDA } from '../../../services/admin/pos.cashier.service';
import { Error } from '../../../models/error.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { Message } from '../../../models/message.model';
import { legendIconInvalid, legendInvalid } from '../../../utils/legend.data.util';
import { getError, getMessage } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { formatContact } from '../../../scripts/format.value.script';

import AppPreloader from '../../../components/Preloader';
import AppPosCashierForm from './component/PosCashierForm';

export interface PosCreatePageProps {};

let errorResponse: Error, messageResponse: Message;

const PosCreatePage: React.FunctionComponent<PosCreatePageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany, storageCentersForCompany} = UseOutletContextPos()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [code, setCode] = useState({value: '', valid: false})
  const [stockEnabled, setStockEnabled] = useState({value: true, valid: true})
  const [storageCenter, setStorageCenter] = useState({value: '', valid: false, object: null as any})
  const [treasuryCashAccounts, setTreasuryCashAccounts] = useState({value: [] as string[], valid: false})
  const [treasuryCard, setTreasuryCard] = useState({value: '', valid: false, object: null as any})
  const [defaultCashCurrency, setDefaultCashCurrency] = useState({value: 'CHF', valid: true})
  const [contact, setContact] = useState({value: '', valid: false, object: null as any})

  const submitPosEntityCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && name.valid && code.valid && (!stockEnabled.value || storageCenter.valid) && treasuryCashAccounts.valid && treasuryCard.valid && contact.valid) {
      await PosCashierDA.postCashier(companyForUser.id, name.value, code.value, stockEnabled.value, stockEnabled.value ? storageCenter.value : null, treasuryCashAccounts.value, treasuryCard.value, defaultCashCurrency.value, {
        id: contact.value,
        name: formatContact(contact.object),
        type: contact.object?.type ?? 'person',
        uuid: contact.object?.uuid ?? ''
      }).then( (response) => {
        if (response.status === 201) {
          messageResponse = response.data

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate('/main/pos/list', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value) legendIconInvalid('input-name', 'legend-name-required')
        if (!code.value) legendIconInvalid('input-code', 'legend-code-required')
        if (stockEnabled.value && !storageCenter.value) legendInvalid('legend-storage-center-required')
        if (treasuryCashAccounts.value.length === 0) legendInvalid('legend-treasury-cash-accounts-required')
        if (!treasuryCard.value) legendInvalid('legend-treasury-account-required-card')
        if (!contact.value) legendInvalid('legend-contact-required')
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.pos, branch: lang.labels.createPOS}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser
        ?
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <h2>{lang.labels.createNewPOS}</h2>
            </div>
          </div>
        <div className="card-body">
          <AppPosCashierForm name={name} code={code} stockEnabled={stockEnabled} storageCenter={storageCenter} treasuryCashAccounts={treasuryCashAccounts} treasuryCard={treasuryCard} defaultCashCurrency={defaultCashCurrency} contact={contact} setName={setName} setCode={setCode} setStockEnabled={setStockEnabled} setStorageCenter={setStorageCenter} setTreasuryCashAccounts={setTreasuryCashAccounts} setTreasuryCard={setTreasuryCard} setDefaultCashCurrency={setDefaultCashCurrency} setContact={setContact}></AppPosCashierForm>
        </div>
        <div className="card-footer d-flex justify-content-end">
          <button className="btn btn-primary" type="button" data-kt-indicator={loadIndicator} onClick={submitPosEntityCreate}>
            <span className="indicator-label">{lang.labels.createPOS}</span>
            <span className="indicator-progress">
              {lang.labels.pleaseWait}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          </button>
        </div>
      </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default PosCreatePage;
